/**
 * Subscription Store
 *
 * Zustand store for managing subscription state across the app
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import subscriptionService from '../services/subscriptionService';
import { calculateRemainingDays, calculateTotalDays } from '../utils/subscriptionDates';
import type {
  Plan,
  Subscription,
  SubscriptionUsage,
  PlanLimits,
  PlanTier,
  BillingCycle,
} from '../types/subscription';

interface SubscriptionState {
  subscription: Subscription | null;
  currentPlan: Plan | null;
  allPlans: Plan[];
  usage: SubscriptionUsage | null;
  limits: PlanLimits | null;
  usagePercentages: Record<string, number>;

  isLoading: boolean;
  isLoadingPlans: boolean;
  isLoadingUsage: boolean;
  isStartingTrial: boolean;
  isUpgrading: boolean;
  isFetching: boolean;
  hasFetchedOnce: boolean;

  error: string | null;

  isTrialActive: boolean;
  trialDaysRemaining: number;
  trialTotalDays: number;

  fetchSubscription: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  fetchPlans: () => Promise<void>;
  fetchUsage: () => Promise<void>;
  startTrial: (planId?: PlanTier) => Promise<boolean>;
  upgradePlan: (planId: PlanTier, billingCycle?: BillingCycle, promoCode?: string) => Promise<boolean>;
  downgradePlan: (planId: PlanTier) => Promise<boolean>;
  changeBillingCycle: (cycle: BillingCycle) => Promise<boolean>;
  cancelSubscription: (reason: string, feedback?: string) => Promise<boolean>;
  reactivateSubscription: () => Promise<boolean>;
  applyPromoCode: (code: string) => Promise<{ success: boolean; discount?: number; message?: string }>;

  hasFeature: (featureKey: string) => boolean;
  isWithinLimit: (limitKey: keyof PlanLimits) => boolean;
  getUsagePercent: (limitKey: keyof PlanLimits) => number;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  subscription: null,
  currentPlan: null,
  allPlans: [],
  usage: null,
  limits: null,
  usagePercentages: {},
  isLoading: false,
  isLoadingPlans: false,
  isLoadingUsage: false,
  isStartingTrial: false,
  isUpgrading: false,
  isFetching: false,
  hasFetchedOnce: false,
  error: null,
  isTrialActive: false,
  trialDaysRemaining: 0,
  trialTotalDays: 0,
};

let subscriptionFetchPromise: Promise<void> | null = null;
let subscriptionLastFetchedAt = 0;
const SUBSCRIPTION_MIN_REFETCH_MS = 30_000;

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Fetch current subscription status.
       * Prevents overlapping requests while still allowing revalidation.
       */
      fetchSubscription: async () => {
        const now = Date.now();
        const isInitialFetch = !get().hasFetchedOnce;

        if (subscriptionFetchPromise) {
          return subscriptionFetchPromise;
        }

        if (get().hasFetchedOnce && now - subscriptionLastFetchedAt < SUBSCRIPTION_MIN_REFETCH_MS) {
          return;
        }

        if (get().isFetching) {
          console.log('Subscription fetch already in progress, skipping duplicate call');
          return;
        }

        subscriptionFetchPromise = (async () => {
          set({ isFetching: true, isLoading: isInitialFetch, error: null });
          try {
            const data = await subscriptionService.getCurrentSubscription();
            subscriptionLastFetchedAt = Date.now();

            if (!data.subscription) {
              set({
                subscription: null,
                currentPlan: null,
                isTrialActive: false,
                trialDaysRemaining: 0,
                trialTotalDays: 0,
                isLoading: false,
                isFetching: false,
                hasFetchedOnce: true,
              });
              return;
            }

            const trialDaysRemaining = typeof data.subscription.trialDaysRemaining === 'number'
              ? data.subscription.trialDaysRemaining
              : calculateRemainingDays(data.subscription.trial?.trialEndDate);

            const trialTotalDays = typeof data.subscription.trialTotalDays === 'number' && data.subscription.trialTotalDays > 0
              ? data.subscription.trialTotalDays
              : calculateTotalDays(data.subscription.trial?.trialStartDate, data.subscription.trial?.trialEndDate) || 0;

            const isTrialStatus = data.subscription.status === 'trialing' || data.subscription.status === 'trial';

            set({
              subscription: data.subscription,
              currentPlan: data.plan || null,
              isTrialActive: isTrialStatus && trialDaysRemaining > 0,
              trialDaysRemaining,
              trialTotalDays,
              isLoading: false,
              isFetching: false,
              hasFetchedOnce: true,
            });
          } catch (error: any) {
            console.error('Failed to fetch subscription:', error.response?.status, error.response?.data);

            if (error.response?.status === 404) {
              set({
                subscription: null,
                currentPlan: null,
                isTrialActive: false,
                trialDaysRemaining: 0,
                trialTotalDays: 0,
                isLoading: false,
                isFetching: false,
                hasFetchedOnce: true,
              });
            } else if (error.response?.status === 403) {
              set({
                subscription: null,
                currentPlan: null,
                isTrialActive: false,
                trialDaysRemaining: 0,
                trialTotalDays: 0,
                error: error.response?.data?.message || 'You do not have permission to view subscription details. Please contact your agency administrator.',
                isLoading: false,
                isFetching: false,
                hasFetchedOnce: true,
              });
            } else {
              set({
                error: error.response?.data?.message || 'Failed to fetch subscription',
                isLoading: false,
                isFetching: false,
                hasFetchedOnce: true,
              });
            }
          } finally {
            subscriptionFetchPromise = null;
          }
        })();

        return subscriptionFetchPromise;
      },

      /**
       * Force refresh subscription (use after payment or admin-side changes).
       */
      refreshSubscription: async () => {
        console.log('Force refreshing subscription...');
        subscriptionLastFetchedAt = 0;
        set({ isFetching: false });
        await get().fetchSubscription();
      },

      fetchPlans: async () => {
        set({ isLoadingPlans: true, error: null });
        try {
          const plans = await subscriptionService.getPlans();
          set({
            allPlans: plans,
            isLoadingPlans: false,
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to fetch plans',
            isLoadingPlans: false,
          });
        }
      },

      fetchUsage: async () => {
        set({ isLoadingUsage: true });
        try {
          const data = await subscriptionService.getUsage();
          set({
            usage: data.usage,
            limits: data.limits,
            usagePercentages: data.percentages,
            isLoadingUsage: false,
          });
        } catch (error: any) {
          set({ isLoadingUsage: false });
          const errorCode = error.response?.data?.code;
          if (error.response?.status === 403 && errorCode === 'NO_SUBSCRIPTION') {
            console.log('No subscription yet - usage not available');
            return;
          }
          if (error.response?.status === 403 || error.response?.status === 404) {
            console.log('Usage not available:', error.response?.data?.message);
            return;
          }
          console.warn('Failed to fetch usage:', error.message);
        }
      },

      /**
       * Start free trial using the selected plan's configured duration.
       */
      startTrial: async (planId?: PlanTier) => {
        set({ isStartingTrial: true, error: null });
        try {
          const data = await subscriptionService.startTrial({ planId });

          set({
            subscription: data.subscription,
            currentPlan: data.plan,
            isTrialActive: true,
            trialDaysRemaining: data.trialDaysRemaining,
            trialTotalDays: data.subscription?.trialTotalDays || data.trialTotalDays || data.plan?.trial?.durationDays || data.trialDaysRemaining,
            isStartingTrial: false,
            hasFetchedOnce: true,
          });
          return true;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to start trial',
            isStartingTrial: false,
          });
          return false;
        }
      },

      upgradePlan: async (planId: PlanTier, billingCycle?: BillingCycle, promoCode?: string) => {
        set({ isUpgrading: true, error: null });
        try {
          const data = await subscriptionService.upgradePlan({
            planId,
            billingCycle,
            promoCode,
          });

          set({
            subscription: data.subscription,
            currentPlan: data.plan,
            isTrialActive: false,
            trialDaysRemaining: 0,
            trialTotalDays: 0,
            isUpgrading: false,
            hasFetchedOnce: true,
          });
          return true;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to upgrade plan',
            isUpgrading: false,
          });
          return false;
        }
      },

      downgradePlan: async (planId: PlanTier) => {
        set({ isUpgrading: true, error: null });
        try {
          const data = await subscriptionService.downgradePlan(planId);
          set({
            subscription: data.subscription,
            isUpgrading: false,
          });
          return true;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to downgrade plan',
            isUpgrading: false,
          });
          return false;
        }
      },

      changeBillingCycle: async (cycle: BillingCycle) => {
        set({ isLoading: true, error: null });
        try {
          const data = await subscriptionService.changeBillingCycle(cycle);
          set({
            subscription: data.subscription,
            isLoading: false,
          });
          return true;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to change billing cycle',
            isLoading: false,
          });
          return false;
        }
      },

      cancelSubscription: async (reason: string, feedback?: string) => {
        set({ isLoading: true, error: null });
        try {
          const data = await subscriptionService.cancelSubscription({
            reason,
            feedback,
            scheduledEnd: true,
          });
          set({
            subscription: data.subscription,
            isLoading: false,
          });
          return true;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to cancel subscription',
            isLoading: false,
          });
          return false;
        }
      },

      reactivateSubscription: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await subscriptionService.reactivateSubscription();
          set({
            subscription: data.subscription,
            isLoading: false,
          });
          return true;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Failed to reactivate subscription',
            isLoading: false,
          });
          return false;
        }
      },

      applyPromoCode: async (code: string) => {
        try {
          const data = await subscriptionService.applyPromoCode(code);
          await get().fetchSubscription();
          return {
            success: true,
            discount: data.discount,
            message: `Promo code applied! You save ${data.discount}${data.discountType === 'percent' ? '%' : ' TND'}`,
          };
        } catch (error: any) {
          return {
            success: false,
            message: error.response?.data?.message || 'Invalid promo code',
          };
        }
      },

      hasFeature: (featureKey: string) => {
        const { currentPlan } = get();
        if (!currentPlan) return false;
        return currentPlan.features.some((feature) => feature.key === featureKey);
      },

      isWithinLimit: (limitKey: keyof PlanLimits) => {
        const { usage, limits } = get();
        if (!usage || !limits) return true;

        const limit = limits[limitKey];
        if (limit === -1) return true;

        const usageKey = limitKey as keyof SubscriptionUsage;
        const currentUsage = usage[usageKey] as number || 0;

        return currentUsage < limit;
      },

      getUsagePercent: (limitKey: keyof PlanLimits) => {
        const { usage, limits } = get();
        if (!usage || !limits) return 0;

        const limit = limits[limitKey];
        if (limit === -1) return 0;

        const usageKey = limitKey as keyof SubscriptionUsage;
        const currentUsage = usage[usageKey] as number || 0;

        return Math.min(100, Math.round((currentUsage / limit) * 100));
      },

      clearError: () => set({ error: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'subscription-storage',
      partialize: (state) => ({
        subscription: state.subscription,
        currentPlan: state.currentPlan,
        isTrialActive: state.isTrialActive,
        trialDaysRemaining: state.trialDaysRemaining,
        trialTotalDays: state.trialTotalDays,
      }),
    }
  )
);

export default useSubscriptionStore;
